<template>
  <span class="query-field-settings">
    <el-button @click="open">筛选项设置</el-button>

    <!-- 等待重置确认期间禁用关闭，避免设置未提交时丢失操作上下文。 -->
    <el-dialog
      title="筛选项设置"
      :width="sortable ? '1040px' : '720px'"
      :top="sortable ? '6vh' : '15vh'"
      :custom-class="sortable ? 'query-field-settings-dialog' : ''"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="!saving"
      :show-close="!saving"
      :visible.sync="visible"
    >
      <template #title>
        <div v-if="sortable" class="query-field-settings__heading">
          <h2>筛选项设置</h2>
          <p>选择需要显示的筛选项，并调整排列顺序</p>
        </div>
        <span v-else class="el-dialog__title">筛选项设置</span>
      </template>

      <div v-if="sortable" class="query-field-settings__editor">
        <section class="query-field-settings__available">
          <!-- 批量操作作用于全部筛选项，搜索只过滤列表；取消全选仍保留必填项。 -->
          <div class="query-field-settings__available-header">
            <h3 class="query-field-settings__section-title">
              全部筛选项
              <span>{{ fields.length }}项</span>
            </h3>
            <span class="query-field-settings__bulk-actions">
              <el-button
                type="text"
                :disabled="saving || checkedAll || !fields.length"
                @click="toggleAll(true)"
              >
                全选
              </el-button>
              <el-button
                type="text"
                title="取消所有非必填筛选项"
                :disabled="
                  saving || selectedProps.length === requiredProps.length
                "
                @click="toggleAll(false)"
              >
                取消全选
              </el-button>
            </span>
          </div>
          <el-input
            v-model="keyword"
            class="query-field-settings__search"
            size="medium"
            prefix-icon="el-icon-search"
            placeholder="搜索筛选项"
            aria-label="搜索筛选项"
            clearable
            :disabled="saving"
          />
          <div class="query-field-settings__choices">
            <el-checkbox-group
              v-model="selectedProps"
              class="query-field-settings__choice-list"
            >
              <!-- 必填项禁止取消；等待重置结果时冻结草稿，避免重复提交。 -->
              <el-checkbox
                v-for="field in filteredFields"
                :key="field.prop"
                :label="field.prop"
                :disabled="field.required || saving"
                :title="
                  field.required
                    ? field.label + '（必填条件不可隐藏）'
                    : field.label
                "
              >
                {{ field.label }}
                <span v-if="field.required">（必填）</span>
              </el-checkbox>
            </el-checkbox-group>
            <p
              v-if="!filteredFields.length"
              class="query-field-settings__empty"
            >
              没有匹配的筛选项
            </p>
          </div>
        </section>

        <section class="query-field-settings__selected">
          <h3 class="query-field-settings__section-title">
            已显示
            <span>{{ selectedFields.length }}项</span>
          </h3>
          <p class="query-field-settings__hint">
            拖动调整顺序，靠近上下边缘自动滚动
          </p>
          <div class="query-field-settings__selected-scroll">
            <!-- 兼容当前 Sortable 版本，由排序库接管边缘滚动。 -->
            <draggable
              v-model="selectedFields"
              class="query-field-settings__selected-list"
              :animation="160"
              :disabled="saving"
              :force-fallback="true"
              :scroll-sensitivity="64"
              :scroll-speed="16"
              :bubble-scroll="false"
              handle=".query-field-settings__handle"
              ghost-class="query-field-settings__ghost"
            >
              <div
                v-for="(field, index) in selectedFields"
                :key="field.prop"
                class="query-field-settings__selected-row"
              >
                <span
                  class="query-field-settings__handle"
                  title="按住拖动调整顺序"
                  aria-hidden="true"
                >
                  <svg-icon icon-class="i-drag" />
                </span>
                <span class="query-field-settings__position">
                  {{ index + 1 }}
                </span>
                <span class="query-field-settings__name" :title="field.label">
                  {{ field.label }}
                </span>
                <span class="query-field-settings__moves">
                  <el-button
                    type="text"
                    icon="el-icon-top"
                    :aria-label="'上移' + field.label"
                    :disabled="saving || index === 0"
                    @click="move(index, -1)"
                  />
                  <el-button
                    type="text"
                    icon="el-icon-bottom"
                    :aria-label="'下移' + field.label"
                    :disabled="saving || index === selectedFields.length - 1"
                    @click="move(index, 1)"
                  />
                </span>
                <!-- 必填条件仍可拖拽或箭头排序，只禁用移除。 -->
                <el-button
                  class="query-field-settings__remove"
                  type="text"
                  icon="el-icon-close"
                  :aria-label="'移除' + field.label"
                  :disabled="field.required || saving"
                  @click="remove(field.prop)"
                />
              </div>
            </draggable>
            <div
              v-if="!selectedFields.length"
              class="query-field-settings__empty"
            >
              <p>还没有显示的筛选项</p>
              <span>从左侧勾选需要的条件</span>
            </div>
          </div>
        </section>
      </div>

      <template v-else>
        <div class="query-field-settings__header">
          <el-checkbox
            :indeterminate="indeterminate"
            :value="checkedAll"
            @change="toggleAll"
          >
            全选
          </el-checkbox>
          <el-button type="text" @click="restoreDefault">恢复默认</el-button>
        </div>
        <el-checkbox-group
          v-model="selectedProps"
          class="query-field-settings__legacy-list"
        >
          <el-checkbox
            v-for="field in fields"
            :key="field.prop"
            :label="field.prop"
          >
            {{ field.label }}
          </el-checkbox>
        </el-checkbox-group>
      </template>

      <template #footer>
        <div :class="{ 'query-field-settings__footer': sortable }">
          <!-- 确认过程中保留弹窗，只有收到成功结果才关闭。 -->
          <el-button
            v-if="sortable"
            type="text"
            :disabled="saving"
            @click="restoreDefault"
          >
            恢复默认
          </el-button>
          <span>
            <el-button
              :size="sortable ? 'medium' : undefined"
              :disabled="saving"
              @click="visible = false"
            >
              取消
            </el-button>
            <el-button
              type="primary"
              :size="sortable ? 'medium' : undefined"
              :loading="saving"
              @click="confirm"
            >
              {{ sortable ? '应用设置' : '确定' }}
            </el-button>
          </span>
        </div>
      </template>
    </el-dialog>
  </span>
</template>

<script>
import draggable from 'vuedraggable'
import { orderQueryFields } from '@/utils/queryFieldSettings'

export default {
  name: 'QueryFieldSettings',
  components: { draggable },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    hiddenFields: {
      type: Array,
      default: () => []
    },
    order: {
      type: Array,
      default: () => []
    },
    sortable: {
      type: Boolean,
      default: false
    },
    // 新版由调用方确认是否允许提交；旧页面继续使用原 change 事件。
    beforeConfirm: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      visible: false,
      selectedProps: [],
      keyword: '',
      // 暂存批量取消前的顺序，重新全选时恢复本次尚未保存的拖拽结果。
      bulkSelectionOrder: [],
      // 等待页面重置通知时锁定弹窗操作，防止草稿与实际提交内容不一致。
      saving: false
    }
  },
  computed: {
    selectedFields: {
      get() {
        const fields = new Map(this.fields.map((field) => [field.prop, field]))
        return this.selectedProps
          .map((prop) => fields.get(prop))
          .filter(Boolean)
      },
      set(fields) {
        this.selectedProps = fields.map(({ prop }) => prop)
      }
    },
    filteredFields() {
      const keyword = this.keyword.trim().toLowerCase()
      return this.fields.filter(({ label }) =>
        label.toLowerCase().includes(keyword)
      )
    },
    allProps() {
      return this.fields.map(({ prop }) => prop)
    },
    // 保留必填字段标识，供打开弹窗、动态规则变化和提交时共同保护。
    requiredProps() {
      return this.fields
        .filter(({ required }) => required)
        .map(({ prop }) => prop)
    },
    checkedAll() {
      return (
        this.allProps.length > 0 &&
        this.selectedProps.length === this.allProps.length
      )
    },
    indeterminate() {
      return (
        this.selectedProps.length > 0 &&
        this.selectedProps.length < this.allProps.length
      )
    }
  },
  watch: {
    // 动态 required 或表单 rules 变化后，已打开的面板也必须立即勾选必填项。
    requiredProps(props) {
      for (const prop of props) {
        if (!this.selectedProps.includes(prop)) this.selectedProps.push(prop)
      }
    }
  },
  methods: {
    open() {
      const hidden = new Set(this.hiddenFields)
      this.selectedProps = orderQueryFields(this.fields, this.order)
        // 缓存不能使必填项处于“禁用但未勾选”的状态。
        .filter(({ prop, required }) => required || !hidden.has(prop))
        .map(({ prop }) => prop)
      this.keyword = ''
      this.bulkSelectionOrder = []
      this.visible = true
    },
    toggleAll(checked) {
      if (this.saving) return
      if (checked) {
        // 全选补齐未选项，保留拖拽顺序；搜索关键词不限制本次批量选择范围。
        this.selectedProps = orderQueryFields(this.fields, [
          ...this.bulkSelectionOrder,
          ...this.selectedProps,
          ...this.order
        ]).map(({ prop }) => prop)
        this.bulkSelectionOrder = []
      } else {
        // 取消全选也保留必填项，普通旧页面没有 required 标识时保持原行为。
        this.bulkSelectionOrder = [...this.selectedProps]
        this.selectedProps = [...this.requiredProps]
      }
    },
    restoreDefault() {
      this.selectedProps = [...this.allProps]
      this.keyword = ''
      // 恢复默认同时清除批量取消记录，之后继续按默认顺序选择。
      this.bulkSelectionOrder = []
    },
    move(index, step) {
      const target = index + step
      if (target < 0 || target >= this.selectedProps.length) return
      const [prop] = this.selectedProps.splice(index, 1)
      this.selectedProps.splice(target, 0, prop)
    },
    remove(prop) {
      // 方法层同步保护，避免键盘调用或直接调用绕过禁用按钮。
      if (this.requiredProps.includes(prop)) return
      this.selectedProps = this.selectedProps.filter((value) => value !== prop)
    },
    async confirm() {
      if (this.saving) return
      // 最后一道保护：提交时再次补回必填项，组合字段由表单统一判定 required。
      const selected = new Set([...this.selectedProps, ...this.requiredProps])
      const hidden = this.allProps.filter((prop) => !selected.has(prop))
      const order = [
        ...this.selectedProps,
        ...orderQueryFields(this.fields, this.order)
          .filter(({ prop }) => !selected.has(prop))
          .map(({ prop }) => prop)
      ]
      if (!this.beforeConfirm) {
        // 旧调用方继续收到隐藏字段数组，不要求接入本次的确认协议。
        this.$emit('change', hidden, order)
        this.visible = false
        return
      }
      this.saving = true
      try {
        // false/异常均保留弹窗草稿，不提前关闭或写缓存。
        if ((await this.beforeConfirm(hidden, order)) === true) {
          this.visible = false
        }
      } catch (error) {
        this.$message.error('筛选项设置未应用，请重试')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.query-field-settings {
  margin-left: 10px;
}

:deep(.query-field-settings-dialog) {
  display: flex;
  flex-direction: column;
  height: 800px;
  max-height: 88vh;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  border-radius: 8px;

  .el-dialog__header {
    padding: 24px 28px 18px;
    border-bottom: 1px solid #e6ebf5;
  }

  .el-dialog__headerbtn {
    top: 26px;
    right: 28px;
    font-size: 20px;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    padding: 24px 28px;
    overflow: hidden;
    font-size: 14px;
  }

  .el-dialog__footer {
    padding: 16px 28px;
    border-top: 1px solid #e6ebf5;
  }
}

.query-field-settings__heading {
  padding-right: 24px;

  h2 {
    margin: 0;
    color: #303133;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  p {
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 14px;
    line-height: 22px;
  }
}

.query-field-settings__editor {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: 100%;
  min-height: 0;
}

.query-field-settings__available,
.query-field-settings__selected {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.query-field-settings__available {
  padding-right: 24px;
  border-right: 1px solid #e6ebf5;
}

// 将批量按钮固定在左栏标题右侧，长列表滚动时仍能直接操作。
.query-field-settings__available-header {
  display: flex;
  flex-shrink: 0;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  .query-field-settings__section-title {
    margin-bottom: 0;
  }
}

.query-field-settings__bulk-actions {
  display: flex;
  flex-shrink: 0;
  gap: 12px;

  .el-button {
    margin: 0;
    padding: 0;
    font-size: 14px;
  }
}

.query-field-settings__section-title {
  display: flex;
  flex-shrink: 0;
  align-items: baseline;
  gap: 12px;
  margin: 0 0 16px;
  color: #303133;
  font-size: 17px;
  font-weight: 600;
  line-height: 24px;

  span {
    color: #6b7280;
    font-size: 14px;
    font-weight: 400;
  }
}

.query-field-settings__search {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.query-field-settings__choices,
.query-field-settings__selected-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.query-field-settings__choice-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;

  .el-checkbox {
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: 44px;
    margin: 0;
  }

  :deep(.el-checkbox__label) {
    overflow: hidden;
    color: #303133;
    font-size: 14px;
    text-overflow: ellipsis;
  }

  :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
    color: #303133;
  }
}

.query-field-settings__hint {
  flex-shrink: 0;
  margin: -8px 0 16px;
  color: #6b7280;
  line-height: 22px;
}

.query-field-settings__selected-list {
  border-top: 1px solid #e6ebf5;
}

.query-field-settings__selected-row {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 54px;
  padding: 8px 10px;
  border-bottom: 1px solid #e6ebf5;

  &:hover,
  &:focus-within {
    border-radius: 4px;
    background: #eaf4ff;

    .query-field-settings__moves {
      opacity: 1;
    }
  }

  .el-button {
    margin: 0;
    font-size: 16px;
  }

  .el-button:focus-visible {
    outline: 2px solid #91caff;
    outline-offset: 2px;
  }
}

.query-field-settings__handle {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  height: 32px;
  font-size: 16px;
  line-height: 1;
  cursor: grab;
  opacity: 0.6;
}

.query-field-settings__position {
  flex: 0 0 28px;
  border-radius: 4px;
  background: #f0f2f5;
  color: #606266;
  line-height: 28px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.query-field-settings__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #303133;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.query-field-settings__moves {
  display: flex;
  flex: 0 0 56px;
  justify-content: space-between;
  opacity: 0;

  .el-button {
    width: 24px;
  }
}

.query-field-settings__remove {
  width: 24px;
  color: #6b7280;

  &:hover,
  &:focus {
    color: #1890ff;
  }
}

.query-field-settings__ghost {
  background: #eaf4ff;
  opacity: 0.4;
}

.query-field-settings__empty {
  padding: 64px 12px;
  color: #6b7280;
  text-align: center;

  p {
    margin: 0 0 8px;
    color: #606266;
  }
}

.query-field-settings__footer,
.query-field-settings__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.query-field-settings__legacy-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 24px;
  margin-top: 20px;

  .el-checkbox {
    margin-right: 0;
  }
}

@media (max-width: 760px) {
  :deep(.query-field-settings-dialog .el-dialog__body) {
    overflow-y: auto;
  }

  .query-field-settings__editor {
    grid-template-columns: 1fr;
    height: auto;
  }

  .query-field-settings__available {
    height: 280px;
    padding: 0 0 24px;
    border-right: 0;
    border-bottom: 1px solid #e6ebf5;
  }

  .query-field-settings__selected {
    height: 320px;
  }
}
</style>
